import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MarkdownModule } from 'ngx-markdown';

export interface HistoryEntry {
  previous_value: any;
  updated_value: any;
  updated_at: string;
  updated_by: string;
  previous_confidence?: boolean;
  updated_confidence?: boolean;
  displayTimestamp?: string;
  displayValue?: string;
  ai_answer?: string;
  category_name?: string;
}

@Component({
  selector: 'app-mapping-result-history-modal',
  standalone: true,
  imports: [MatDialogModule, CommonModule, MatIconModule, MarkdownModule],
  templateUrl: './mapping-result-history-modal.component.html',
  styleUrls: ['./mapping-result-history-modal.component.scss'],
})
export class MappingResultHistoryModalComponent {
  public sortedHistory: HistoryEntry[];
  public expanded: { [index: number]: boolean } = {};
  constructor(
    public dialogRef: MatDialogRef<MappingResultHistoryModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { attributeName: string; history: HistoryEntry[] }
  ) {
    // Build sorted history (newest first), compute a displayValue and displayTimestamp,
    // then remove duplicate displayValues (keep the most recent occurrence).
    const sorted = [...(data.history || [])].sort((a, b) => {
      const ta = a && a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const tb = b && b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return tb - ta;
    }).map(h => ({
      ...h,
      displayTimestamp: this.formatRawTimestamp(h.updated_at),
      // prefer previous_value (show what was before the change), then updated_value, then ai_answer
      displayValue: (h.previous_value ?? h.updated_value ?? h.ai_answer ?? '')
    } as HistoryEntry));

    // Show all entries (newest-first). If you prefer deduplication, reinstate a set-based filter here.
    this.sortedHistory = sorted;
    this.sortedHistory.forEach((_, i) => {
      this.expanded[i] = false;
    });
  }

  close() {
    this.dialogRef.close(false);
  }

  toggleExpand(index: number) {
    this.expanded[index] = !this.expanded[index];
  }

  private formatRawTimestamp(ts: string | undefined | null): string {
    if (!ts) return '-';
    const m = ts.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return ts;
    const [, y, mo, d, hh, mm] = m;
    return `${y}/${mo}/${d}, ${hh}:${mm}`;
  }

  getTruncatedValue(value?: string): string {
    if (!value) return '-';
    if (value.length > 100) {
      return value.slice(0, 100) + '...';
    }
    return value;
  }

  htmlEscape(input?: string | null): string {
    if (input === undefined || input === null) return '';
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/\//g, '&#x2F;')
      .replace(/`/g, '&#96;');
  }
}
